/**
 * `507` — *create {an item, itemColumnValues}* on this board. The name comes from
 * `columnValues.name`; every other value is handed to the service layer, which runs it through
 * the column type's own write transform (doc 04 registry 1) — the engine never interprets a
 * column value itself. Text tokens inside string values are interpolated (§5.7).
 *
 * Overwhelmingly used with trigger `47`, where the event carries no item, so this action needs
 * only `boardId`.
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { interpolateTokens } from '../tokens.js';
import { tokenContextFor } from './token-context.js';
export const createItemParams = z.object({
    itemColumnValues: z
        .object({
        columnValues: z.record(z.string(), z.unknown()).default({}),
        groupId: z.string().nullable().default(null),
        invalid: z.boolean().optional(),
    })
        .default({ columnValues: {}, groupId: null }),
});
export const createItemAction = defineAction({
    id: '507',
    kind: 'action',
    app: 'monday',
    sentencePartial: 'create {an item, itemColumnValues}',
    paramsSchema: createItemParams,
    executable: true,
    references: (params) => [
        ...(params.itemColumnValues.groupId === null ? [] : [{ kind: 'group', id: params.itemColumnValues.groupId }]),
        ...Object.keys(params.itemColumnValues.columnValues)
            .filter((columnId) => columnId !== 'name')
            .map((columnId) => ({ kind: 'column', id: columnId })),
    ],
    async execute(context, params) {
        const { name, ...rest } = params.itemColumnValues.columnValues;
        /*
         * **P-296.** Every string this action is about to interpolate — the name and each string cell
         * value — is handed to `tokenContextFor`, which is what pre-resolves the `{pulse.<columnId>}`
         * and `{<boardId>.pulse.<columnId>}` tokens inside them. Called with no strings (as it was
         * until round 11f) nothing is pre-resolved, so a column token could only ever render as its
         * own literal and only `{pulse.name}` / `{board.name}` / `{user.name}` worked.
         */
        const texts = [name, ...Object.values(rest)].filter((value) => typeof value === 'string');
        const tokens = await tokenContextFor(context, texts);
        const columnValues = {};
        for (const [columnId, value] of Object.entries(rest)) {
            columnValues[columnId] = typeof value === 'string' ? interpolateTokens(value, tokens) : value;
        }
        const created = await context.services.createItem({
            boardId: context.boardId,
            groupId: params.itemColumnValues.groupId,
            name: interpolateTokens(typeof name === 'string' && name.length > 0 ? name : 'New item', tokens),
            columnValues,
        });
        return { itemId: created.id, groupId: created.groupId, name: created.name };
    },
});
//# sourceMappingURL=507.js.map